import { baseUrl, getTokenFromLocalStorage } from "./Configuration";

export interface DraftFilters {
  types?: string[];
  showingStatuses?: string[];
  appfolioStatuses?: string[];
  threadSizes?: string[];
}

export async function getDraftCount(listingId: string) {
  const res = await fetch(
    `${baseUrl}/api/email-drafts?listingId=${listingId}`,
    {
      headers: {
        Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
      },
    },
  );

  if (!res.ok) return 0;
  const data = await res.json();
  return data.length;
}

export async function getDraftsByListing(listingId: string) {
  const res = await fetch(
    `${baseUrl}/api/email-drafts?listingId=${listingId}`,
    {
      headers: {
        Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
      },
    },
  );
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }
  if (!res.ok) throw new Error("Failed to fetch drafts");
  return res.json();
}

export async function getDraftsFiltered(
  listingId: string,
  filters?: DraftFilters,
) {
  const params = new URLSearchParams();
  params.append("listingId", listingId);

  if (filters) {
    if (filters.types && filters.types.length > 0)
      filters.types.forEach((t) => params.append("types", t));
    if (filters.showingStatuses && filters.showingStatuses.length > 0)
      filters.showingStatuses.forEach((s) =>
        params.append("showingStatuses", s),
      );
    if (filters.appfolioStatuses && filters.appfolioStatuses.length > 0)
      filters.appfolioStatuses.forEach((s) =>
        params.append("appfolioStatuses", s),
      );
    if (filters.threadSizes && filters.threadSizes.length > 0)
      filters.threadSizes.forEach((s) => params.append("threadSizes", s));
  }

  const res = await fetch(`${baseUrl}/api/email-drafts?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
    },
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return [];
  }
  if (!res.ok) throw new Error("Failed to fetch drafts");
  return res.json();
}

export async function sendDraftEmail(
  id: string,
  subject: string,
  body: string,
) {
  const res = await fetch(`${baseUrl}/api/email-drafts/send/${id}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
    },
    body: JSON.stringify({
      subject,
      body,
    }),
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }
  if (!res.ok) throw new Error("Failed to send email");
}

export async function rememberEmailEdit(
  receiverEmail: string,
  listingId: string | undefined,
  subject: string,
  editedBody: string,
  originalBody?: string,
  notes?: string,
) {
  const res = await fetch(`${baseUrl}/api/email-drafts/remember`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
    },
    body: JSON.stringify({
      receiverEmail,
      listingId: listingId || null,
      subject,
      editedBody,
      originalBody,
      notes,
    }),
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }
  if (!res.ok) throw new Error("Failed to remember email edit");
  return res.json();
}

export interface RememberedEmailFilters {
  listingId?: string;
  types?: string[];
  showingStatuses?: string[];
  appfolioStatuses?: string[];
  apartments?: string[];
}

export async function getRememberedEmails(filters?: RememberedEmailFilters) {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.listingId) params.append("listingId", filters.listingId);
    if (filters.types && filters.types.length > 0)
      filters.types.forEach((t) => params.append("types", t));
    if (filters.showingStatuses && filters.showingStatuses.length > 0)
      filters.showingStatuses.forEach((s) =>
        params.append("showingStatuses", s),
      );
    if (filters.appfolioStatuses && filters.appfolioStatuses.length > 0)
      filters.appfolioStatuses.forEach((s) =>
        params.append("appfolioStatuses", s),
      );
    if (filters.apartments && filters.apartments.length > 0)
      filters.apartments.forEach((a) => params.append("apartments", a));
  }

  const queryString = params.toString();
  const url = queryString
    ? `${baseUrl}/api/email-drafts/remembered?${queryString}`
    : `${baseUrl}/api/email-drafts/remembered`;

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
    },
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }
  if (!res.ok) throw new Error("Failed to fetch remembered emails");
  return res.json();
}

export async function deleteRememberedEmail(id: string) {
  const res = await fetch(`${baseUrl}/api/email-drafts/remembered/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
    },
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }
  if (!res.ok) throw new Error("Failed to delete remembered email");
}

export async function updateRememberedEmailNotes(id: string, notes: string) {
  const res = await fetch(
    `${baseUrl}/api/email-drafts/remembered/${id}/notes`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
      },
      body: JSON.stringify({ notes }),
    },
  );
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }
  if (!res.ok) throw new Error("Failed to update notes");
  return res.json();
}

export async function getApartmentsForFilter() {
  const res = await fetch(`${baseUrl}/api/email-drafts/apartments`, {
    headers: {
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
    },
  });
  if (res.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    return [];
  }
  if (!res.ok) throw new Error("Failed to fetch apartments");
  return res.json();
}
